import { isPlatformServer } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { ActivatedRouteSnapshot, UrlTree } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { getFullPath } from '../../lib/get-full-path';
import { GalleryService } from '../../services/gallery.service';
import { RedirectService } from '../../services/redirect.service';
import { StateService } from '../../services/state.service';
import { UserService } from '../../services/user.service';
import { ViewGalleryGuard } from './view-gallery.guard';

@Injectable({
  providedIn: 'root',
})
export class GalleryIdentifyGuard {
  private galleryService = inject(GalleryService);
  private redirectService = inject(RedirectService);
  private userService = inject(UserService);
  private state = inject(StateService);
  private guard = inject(ViewGalleryGuard);
  private platformId = inject<string>(PLATFORM_ID);

  async canActivate(next: ActivatedRouteSnapshot): Promise<UrlTree | boolean> {
    if (isPlatformServer(this.platformId)) return true;

    const resp = await this.guard.handleGalleryPath(next, true);
    if (resp !== true) return resp;

    const path = getFullPath(next);
    try {
      const gallery = await this.galleryService.getPublicGalleryFromSnapshot(next, { isPublic: true });
      const guestLoggedIn = !!this.state.guestAuthToken;
      const userLoggedIn = !!(await firstValueFrom(this.userService.getUser()));

      if (gallery?.requires_user_identification && !guestLoggedIn && !userLoggedIn) return true;
      return this.redirectService.redirectTo(
        path.filter(u => u !== 'identify'),
        { queryParams: next.queryParams }
      );
    } catch (error) {
      if (!(error instanceof HttpErrorResponse)) throw error;
      return this.redirectService.redirectTo(
        path.filter(u => u !== 'identify'),
        { queryParams: next.queryParams }
      );
    }
  }
}
